import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface User {
  id: string
  username: string
  email: string
  avatar: string
  joinedDate: string
  favorites: string[]
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => boolean
  register: (username: string, email: string, password: string) => boolean
  logout: () => void
  toggleFavorite: (contentId: string) => void
  isFavorite: (contentId: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)

  useEffect(() => {
    const storedUser = localStorage.getItem('magicCartoonUser')
    if (storedUser) {
      setUser(JSON.parse(storedUser))
    }
  }, [])

  const login = (email: string, password: string) => {
    const users = JSON.parse(localStorage.getItem('magicCartoonUsers') || '[]')
    const foundUser = users.find((u: any) => u.email === email && u.password === password)
    
    if (foundUser) {
      const userToStore = { ...foundUser }
      delete userToStore.password
      setUser(userToStore)
      localStorage.setItem('magicCartoonUser', JSON.stringify(userToStore))
      return true
    }
    return false
  }

  const register = (username: string, email: string, password: string) => {
    const users = JSON.parse(localStorage.getItem('magicCartoonUsers') || '[]')
    
    if (users.some((u: any) => u.email === email)) {
      return false
    }

    const newUser = {
      id: Date.now().toString(),
      username,
      email,
      password,
      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${username}`,
      joinedDate: new Date().toISOString(),
      favorites: []
    }

    users.push(newUser)
    localStorage.setItem('magicCartoonUsers', JSON.stringify(users))
    
    const userToStore = { ...newUser }
    delete userToStore.password
    setUser(userToStore)
    localStorage.setItem('magicCartoonUser', JSON.stringify(userToStore))
    return true
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem('magicCartoonUser')
  }

  const toggleFavorite = (contentId: string) => {
    if (!user) return

    const updatedFavorites = user.favorites.includes(contentId)
      ? user.favorites.filter(id => id !== contentId)
      : [...user.favorites, contentId]

    const updatedUser = { ...user, favorites: updatedFavorites }
    setUser(updatedUser)
    localStorage.setItem('magicCartoonUser', JSON.stringify(updatedUser))

    const users = JSON.parse(localStorage.getItem('magicCartoonUsers') || '[]')
    const userIndex = users.findIndex((u: any) => u.id === user.id)
    if (userIndex !== -1) {
      users[userIndex].favorites = updatedFavorites
      localStorage.setItem('magicCartoonUsers', JSON.stringify(users))
    }
  }

  const isFavorite = (contentId: string) => {
    return user?.favorites.includes(contentId) || false
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout, toggleFavorite, isFavorite }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
